import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { toast } from 'sonner';
import { Heart, Clock, Users, Trash2, Search, Loader2, Plus, Calendar } from 'lucide-react';
import MobileFavorites from '@/components/MobileFavorites';

const Favorites = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const loadFavorites = async () => {
      try {
        const response = await api.get('/favorites');
        setFavorites(response.data);
      } catch (error) {
        console.error('Failed to load favorites:', error);
        toast.error('Failed to load favorites');
      } finally {
        setLoading(false);
      }
    };

    loadFavorites();
  }, [api]);

  const removeFavorite = async (recipeId, e) => {
    e?.preventDefault();
    e?.stopPropagation();

    try {
      await api.delete(`/favorites/${recipeId}`);
      setFavorites(favorites.filter(f => f.recipe_id !== recipeId));
      toast.success('Removed from favorites');
    } catch (error) {
      toast.error('Failed to remove favorite');
    }
  };

  const addToMealPlan = async (recipe, date) => {
    try {
      await api.post('/meal-plans', {
        recipe_id: recipe.recipe_id,
        recipe_title: recipe.recipe_title,
        recipe_image: recipe.recipe_image,
        date: date,
        meal_type: 'dinner'
      });
      toast.success('Added to meal plan!');
    } catch (error) {
      toast.error('Failed to add to meal plan');
    }
  };

  // Render mobile version
  if (isMobile) {
    return (
      <MobileFavorites 
        favorites={favorites}
        loading={loading}
        onRemoveFavorite={removeFavorite}
        onAddToMealPlan={addToMealPlan}
      />
    );
  }

  // Desktop version
  return (
    <div className="min-h-screen py-8" data-testid="favorites-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-semibold font-['Playfair_Display'] mb-2">
            My Favorites
          </h1>
          <p className="text-muted-foreground">
            Your saved recipes for quick access
          </p>
        </div>

        {/* Favorites Grid */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Card key={i} className="border-border/50 overflow-hidden animate-pulse">
                <div className="aspect-[4/3] bg-muted" />
                <CardContent className="pt-4">
                  <div className="h-5 bg-muted rounded mb-2" />
                  <div className="h-4 bg-muted rounded w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : favorites.length > 0 ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {favorites.map((fav) => (
              <Card 
                key={fav.id} 
                className="border-border/50 overflow-hidden recipe-card group"
                data-testid={`favorite-${fav.recipe_id}`}
              >
                <Link to={`/recipes/${fav.recipe_id}`}>
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img
                      src={fav.recipe_image}
                      alt={fav.recipe_title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute top-2 right-2 flex gap-1">
                      <Button
                        variant="secondary"
                        size="icon"
                        className="h-8 w-8 bg-white/90 hover:bg-white opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          // Quick add to today
                          const today = new Date().toISOString().split('T')[0];
                          addToMealPlan(fav, today);
                        }}
                        data-testid={`quick-add-${fav.recipe_id}`}
                      >
                        <Calendar className="h-4 w-4 text-primary" />
                      </Button>
                      <Button
                        variant="secondary"
                        size="icon"
                        className="h-8 w-8 bg-white/90 hover:bg-white"
                        onClick={(e) => removeFavorite(fav.recipe_id, e)}
                        data-testid={`remove-favorite-${fav.recipe_id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </Link>
                <CardContent className="pt-4">
                  <Link to={`/recipes/${fav.recipe_id}`}>
                    <h3 className="font-medium line-clamp-2 group-hover:text-primary transition-colors mb-2">
                      {fav.recipe_title}
                    </h3>
                  </Link>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      {fav.ready_in_minutes && (
                        <span className="flex items-center gap-1">
                          <Clock className="h-3.5 w-3.5" />
                          {fav.ready_in_minutes} min
                        </span>
                      )}
                      {fav.servings && (
                        <span className="flex items-center gap-1">
                          <Users className="h-3.5 w-3.5" />
                          {fav.servings}
                        </span>
                      )}
                    </div>
                    <Button 
                      size="sm" 
                      variant="ghost"
                      className="h-8 px-2"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        navigate('/meal-planner', { state: { suggestedRecipe: fav } });
                      }}
                    >
                      <Plus className="h-4 w-4 mr-1" />
                      Add
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="border-border/50">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <Heart className="h-16 w-16 text-muted-foreground/30 mb-4" />
              <h3 className="text-lg font-medium mb-2">No favorites yet</h3>
              <p className="text-muted-foreground text-center mb-6 max-w-md">
                Start exploring recipes and save your favorites to access them quickly
              </p>
              <Link to="/recipes">
                <Button data-testid="find-recipes-btn">
                  <Search className="h-4 w-4 mr-2" />
                  Find Recipes
                </Button>
              </Link>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default Favorites;
